import { apiFetch } from '../lib/apiFetch';
import type {
  InventoryRegion,
  TreeSpecies,
  StockResponse,
  RegionForecast,
  SetStockPayload,
  CloseStatusResponse,
  ClosePeriodSummary,
  SubmissionDetail,
  RecordEmptySubmissionPayload,
  UpdateSubmissionPayload,
} from '../types/shared';

export type {
  InventoryRegion, TreeSpecies, StockResponse, RegionForecast, SetStockPayload,
  CloseStatusResponse, ClosePeriodSummary, SubmissionDetail,
  RecordEmptySubmissionPayload, UpdateSubmissionPayload,
};

// ---------------------------------------------------------------------------
// Warehouse types
// ---------------------------------------------------------------------------

export interface Warehouse {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

export interface WarehouseRegion {
  warehouseId: string;
  regionId: string;
}

export interface WarehouseTransfer {
  id: string;
  inventoryItemId: string;
  sourceWarehouseId: string;
  destWarehouseId: string;
  quantity: number;
  status: 'pending' | 'shipped' | 'received';
  shippedAt: string | null;
  receivedAt: string | null;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReorderPoint {
  id: string;
  warehouseId: string;
  inventoryItemId: string;
  defaultReorderThreshold: number;
  defaultReorderQuantity: number;
  overrideReorderThreshold: number | null;
  overrideReorderQuantity: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/warehouses
// ---------------------------------------------------------------------------
export async function fetchWarehouses(): Promise<Warehouse[]> {
  const res = await apiFetch('/api/v1/inventory/warehouses');
  if (!res.ok) throw new Error(`Failed to load warehouses: ${res.status}`);
  const data = await res.json() as { warehouses: Warehouse[] };
  return data.warehouses;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/warehouses/:id/regions
// ---------------------------------------------------------------------------
export async function fetchWarehouseRegions(warehouseId: string): Promise<WarehouseRegion[]> {
  const res = await apiFetch(`/api/v1/inventory/warehouses/${warehouseId}/regions`);
  if (!res.ok) throw new Error(`Failed to load warehouse regions: ${res.status}`);
  const data = await res.json() as { regions: WarehouseRegion[] };
  return data.regions;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/warehouses/:id/restock-source
// ---------------------------------------------------------------------------
export async function fetchWarehouseRestockSource(warehouseId: string): Promise<string | null> {
  const res = await apiFetch(`/api/v1/inventory/warehouses/${warehouseId}/restock-source`);
  if (!res.ok) throw new Error(`Failed to load warehouse restock source: ${res.status}`);
  const data = await res.json() as { sourceWarehouseId: string | null };
  return data.sourceWarehouseId;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/regions (deprecated, kept for backwards compatibility)
// ---------------------------------------------------------------------------
export async function fetchInventoryRegions(): Promise<InventoryRegion[]> {
  const res = await apiFetch('/api/v1/inventory/regions');
  if (!res.ok) throw new Error(`Failed to load inventory regions: ${res.status}`);
  const data = await res.json() as { regions: InventoryRegion[] };
  return data.regions;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/species
// ---------------------------------------------------------------------------
export async function fetchSpecies(): Promise<TreeSpecies[]> {
  const res = await apiFetch('/api/v1/inventory/species');
  if (!res.ok) throw new Error(`Failed to load species list: ${res.status}`);
  const data = await res.json() as { species: TreeSpecies[] };
  return data.species;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/stock
// Returns { species, warehouses } — species is the master list, warehouses have per-warehouse stock
// ---------------------------------------------------------------------------
export async function fetchStockLevels(): Promise<StockResponse> {
  const res = await apiFetch('/api/v1/inventory/stock');
  if (!res.ok) throw new Error(`Failed to load stock levels: ${res.status}`);
  return res.json() as Promise<StockResponse>;
}

// ---------------------------------------------------------------------------
// POST /api/v1/inventory/stock/set
// Updated to use warehouseId instead of regionId
// ---------------------------------------------------------------------------
export async function setStockLevel(payload: SetStockPayload): Promise<void> {
  const res = await apiFetch('/api/v1/inventory/stock/set', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(err.message ?? `Failed to set stock level: ${res.status}`);
  }
}

// ---------------------------------------------------------------------------
// POST /api/v1/inventory/transfers
// ---------------------------------------------------------------------------
export async function createWarehouseTransfer(payload: {
  sourceWarehouseId: string;
  destWarehouseId:   string;
  inventoryItemId:   string;
  quantity:          number;
  notes?:            string;
}): Promise<{ transferId: string; status: string }> {
  const res = await apiFetch('/api/v1/inventory/transfers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(err.message ?? `Failed to create transfer: ${res.status}`);
  }
  return res.json() as Promise<{ transferId: string; status: string }>;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/transfers
// ---------------------------------------------------------------------------
export async function fetchWarehouseTransfers(filters?: {
  sourceWarehouseId?: string;
  destWarehouseId?:   string;
  status?:            string;
}): Promise<WarehouseTransfer[]> {
  const params = new URLSearchParams();
  if (filters?.sourceWarehouseId) params.append('sourceWarehouseId', filters.sourceWarehouseId);
  if (filters?.destWarehouseId) params.append('destWarehouseId', filters.destWarehouseId);
  if (filters?.status) params.append('status', filters.status);

  const queryString = params.toString();
  const url = `/api/v1/inventory/transfers${queryString ? `?${queryString}` : ''}`;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(`Failed to load transfers: ${res.status}`);
  const data = await res.json() as { transfers: WarehouseTransfer[] };
  return data.transfers;
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/inventory/transfers/:id/status
// ---------------------------------------------------------------------------
export async function updateTransferStatus(
  transferId: string,
  status: 'pending' | 'shipped' | 'received',
): Promise<void> {
  const res = await apiFetch(`/api/v1/inventory/transfers/${transferId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(err.message ?? `Failed to update transfer status: ${res.status}`);
  }
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/forecast
// Returns region-level forecasts (internally computed from warehouse data)
// ---------------------------------------------------------------------------
export async function fetchForecast(): Promise<RegionForecast[]> {
  const res = await apiFetch('/api/v1/inventory/forecast');
  if (!res.ok) throw new Error(`Failed to load forecast: ${res.status}`);
  const data = await res.json() as { forecast: RegionForecast[] };
  return data.forecast;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/warehouses/:id/reorder-points
// ---------------------------------------------------------------------------
export async function fetchReorderPoints(warehouseId: string): Promise<ReorderPoint[]> {
  const res = await apiFetch(`/api/v1/inventory/warehouses/${warehouseId}/reorder-points`);
  if (!res.ok) throw new Error(`Failed to load reorder points: ${res.status}`);
  const data = await res.json() as { reorderPoints: ReorderPoint[] };
  return data.reorderPoints;
}

// ---------------------------------------------------------------------------
// POST /api/v1/inventory/warehouses/:id/reorder-points
// ---------------------------------------------------------------------------
export async function upsertReorderPoint(
  warehouseId: string,
  payload: {
    inventoryItemId:          string;
    defaultReorderThreshold:   number;
    defaultReorderQuantity:    number;
    overrideReorderThreshold?: number | null;
    overrideReorderQuantity?:  number | null;
    notes?:                    string;
  },
): Promise<{ reorderPointId: string; warehouseId: string }> {
  const res = await apiFetch(`/api/v1/inventory/warehouses/${warehouseId}/reorder-points`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(err.message ?? `Failed to upsert reorder point: ${res.status}`);
  }
  return res.json() as Promise<{ reorderPointId: string; warehouseId: string }>;
}

// ===========================================================================
// Monthly Inventory Close (Phase 2 — backend) client functions
// ===========================================================================

async function readError(res: Response, fallback: string): Promise<never> {
  const err = await res.json().catch(() => ({})) as { message?: string };
  throw new Error(err.message ?? `${fallback}: ${res.status}`);
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/close?regionId=&period=YYYY-MM
// ---------------------------------------------------------------------------
export async function fetchCloseStatus(
  regionId: string,
  period:   string,
): Promise<CloseStatusResponse> {
  const url = `/api/v1/inventory/close?regionId=${encodeURIComponent(regionId)}&period=${encodeURIComponent(period)}`;
  const res = await apiFetch(url);
  if (!res.ok) await readError(res, 'Failed to load close status');
  return res.json() as Promise<CloseStatusResponse>;
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/closes?regionId=
// ---------------------------------------------------------------------------
export async function fetchClosesHistory(regionId: string): Promise<ClosePeriodSummary[]> {
  const res = await apiFetch(`/api/v1/inventory/closes?regionId=${encodeURIComponent(regionId)}`);
  if (!res.ok) await readError(res, 'Failed to load close history');
  const data = await res.json() as { closes: ClosePeriodSummary[] };
  return data.closes;
}

// ---------------------------------------------------------------------------
// POST /api/v1/inventory/close/:regionId/:period/approve
// ---------------------------------------------------------------------------
export async function approvePeriod(
  regionId: string,
  period:   string,
  notes?:   string,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/inventory/close/${encodeURIComponent(regionId)}/${encodeURIComponent(period)}/approve`,
    { method: 'POST', body: JSON.stringify({ notes }) },
  );
  if (!res.ok) await readError(res, 'Failed to approve period');
}

// ---------------------------------------------------------------------------
// POST /api/v1/inventory/close/:regionId/:period/reopen
// ---------------------------------------------------------------------------
export async function reopenPeriod(
  regionId: string,
  period:   string,
  reason:   string,
): Promise<void> {
  const res = await apiFetch(
    `/api/v1/inventory/close/${encodeURIComponent(regionId)}/${encodeURIComponent(period)}/reopen`,
    { method: 'POST', body: JSON.stringify({ reason }) },
  );
  if (!res.ok) await readError(res, 'Failed to re-open period');
}

// ---------------------------------------------------------------------------
// POST /api/v1/inventory/submissions/empty
// ---------------------------------------------------------------------------
export async function recordEmptySubmission(
  payload: RecordEmptySubmissionPayload,
): Promise<{ submissionId: string }> {
  const res = await apiFetch('/api/v1/inventory/submissions/empty', {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!res.ok) await readError(res, 'Failed to record empty submission');
  return res.json() as Promise<{ submissionId: string }>;
}

// ---------------------------------------------------------------------------
// PATCH /api/v1/inventory/submissions/:id
// ---------------------------------------------------------------------------
export async function updateSubmission(
  submissionId: string,
  patch:        UpdateSubmissionPayload,
): Promise<void> {
  const res = await apiFetch(`/api/v1/inventory/submissions/${encodeURIComponent(submissionId)}`, {
    method: 'PATCH',
    body:   JSON.stringify(patch),
  });
  if (!res.ok) await readError(res, 'Failed to update submission');
}

// ---------------------------------------------------------------------------
// GET /api/v1/inventory/submissions/:id
// ---------------------------------------------------------------------------
export async function fetchSubmissionDetail(submissionId: string): Promise<SubmissionDetail> {
  const res = await apiFetch(`/api/v1/inventory/submissions/${encodeURIComponent(submissionId)}`);
  if (!res.ok) await readError(res, 'Failed to load submission detail');
  return res.json() as Promise<SubmissionDetail>;
}
